import { useCallback, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import type { OfferListAndAction, OfferListV2Item } from 'components/OfferList/OfferList'
import OfferList, { OfferListAction } from 'components/OfferList/OfferList'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'
import type { RouteComponentProps } from 'react-router'
import { withRouter } from 'react-router'
import type { ConnectedProps } from 'react-redux'
import { connect } from 'react-redux'
import styled from 'styled-components'
import clsx from 'clsx'
import LayoutContainer from 'components/Common/LayoutContainer'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import {
  deleteSearchParams,
  parseSearchString,
  setSearchParamValue,
  setManySearchParamValues,
} from 'lib/url/searchUrlUtils'
import type { EnabledFilters } from 'components/Search/type'
import config from 'constants/config'
import type {
  FilterControlChangeHandler,
} from 'components/Pages/HotelSearchPage/HotelSearchPageFilterPane'
import HotelSearchPageFilterPane from 'components/Pages/HotelSearchPage/HotelSearchPageFilterPane'
import { ANYWHERE_NAME, ANYWHERE_PLACE_ID } from 'constants/search'
import { getSearchKey, getSearchTypeFromFilters } from 'lib/search/searchUtils'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useGlobalSearchURLHashVertical from 'hooks/GlobalSearch/useGlobalSearchURLHashVertical'
import { useScreenSizeOnly, useScreenSizeUp } from 'hooks/useScreenSize'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { replace } from 'connected-react-router'
import type { SearchFormRef } from 'components/Search'
import useOfferList from 'hooks/Offers/useOfferList'
import useOfferListSortOptions from 'hooks/useOfferListSortOptions'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import { getAppliedFilterCount } from 'components/Search/utils'
import HotelSearchNoSearchResultSection from 'components/Pages/HotelSearchPage/HotelSearchNoSearchResultSection'
import OfferCountAndSort from 'components/Pages/HotelSearchPage/OfferCountAndSort'
import OfferRecommendations from 'components/Common/OfferRecommendations/OfferRecommendations'
import useQueryParams from 'hooks/useQueryParams'
import { GLOBAL_SEARCH_INITIAL_STATE } from 'contexts/GlobalSearch/GlobalSearchState'
import HotelSearchHeader from 'components/Pages/HotelSearchPage/HotelSearchHeader'
import { useGlobalHotelsSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalHotelsSearch'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import { SEARCH_STREAMING_PAGE_SIZE } from 'search/initialiseSearchSocket'
import type { SortControlChangeHandler } from 'components/Common/OfferSortControl/OfferSortControl'
import { setSearchEvent } from 'actions/SearchActions'
import { pushWithRegion } from 'actions/NavigationActions'

const OVERRIDE_PAGE_SIZE = 10

const REDIRECTION_PARAMS_MAP: Record<string, Array<{ paramName: string, paramValue: string }>> = {
  '/campaign/wintersun2': [
    { paramName: 'destinationName', paramValue: 'Asia' },
    { paramName: 'destinationId', paramValue: 'le_fdbf7de11c1a054dc927adf66d9b5fce' },
  ],
  '/campaign/wintersun-test': [
    { paramName: 'destinationName', paramValue: 'Asia' },
    { paramName: 'destinationId', paramValue: 'le_fdbf7de11c1a054dc927adf66d9b5fce' },
  ],
}

function hasSearchTarget(params: Record<string, string | undefined>): boolean {
  return !!(
    (params.destinationId && params.destinationName) ||
    (params.landmarkId && params.landmarkName) ||
    (params.propertyId && params.propertyName) ||
    params.bounds ||
    (params.lat && params.lng) ||
    params.query
  )
}

const StlyedPane = styled.div`
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  padding-top: ${rem(24)};

  ${mediaQueryUp.tablet} {
    padding-top: ${rem(32)};
  }

  &.fullWidth {
    background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
    padding-top: ${rem(16)};

    ${mediaQueryUp.tablet} {
      padding-top: ${rem(32)};
    }
  }
`

const StyledLayoutContainer = styled(LayoutContainer)`
  &.filterPane {
    background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
    display: grid;
    padding: ${rem(24)} 0;
    grid-template-columns: minmax(0, 1fr);
    grid-gap: ${rem(32)};

    ${mediaQueryUp.desktop} {
      grid-template: "offer-filters offer-list" auto / ${rem(278)} minmax(0, 1fr);
      padding-top: ${rem(12)};
    }
  }
  &.fullWidth {
    padding: 0;
  }
`

const StyledVerticalSpacer = styled(VerticalSpacer)`
  grid-area: offer-list;
`

const StyledHotelSearchPageFilterPane = styled(HotelSearchPageFilterPane)`
  grid-area: offer-filters;
`

function mapState(state: App.State) {
  const filters = getUrlOfferListFilters(state)

  return {
    windowSearch: state.router.location.search,
    filters,
    memberId: state.auth.account.memberId,
    searchResultMetadata: state.offer.searchResultMetadata,
    isFlexibleSearch: !!(filters?.flexibleMonths && filters?.flexibleNights),
    specificPlaces: state.destination.specificPlaces[filters.destinationId ?? ''],
  }
}

const connector = connect(mapState)

type PropsFromRedux = ConnectedProps<typeof connector>

interface Props extends PropsFromRedux, RouteComponentProps<any>, App.ComponentVerticalOfferList {
  showFilters: boolean
  title: string
  campaigns: Array<string>
  offerTypes: Array<App.OfferListOfferType>
  placeIds: Array<string>
}

function ContentfulVerticalOfferList({
  filters,
  campaigns,
  offerTypes,
  searchResultMetadata,
  windowSearch,
  isFlexibleSearch,
  history,
  specificPlaces,
  showFilters,
  showSearchHeader,
  placeIds,
  isInfiniteScroll,
  offerIds,
}: Props) {
  const hasOverrideOfferIds = !!offerIds?.length
  const liveOverrideOfferCount = useAppSelector(state => {
    if (!offerIds?.length) return 0
    return offerIds.filter(id => {
      if (state.offer.offersLoading[id] || (!state.offer.offers[id] && !state.offer.offerErrors[id])) return true
      return !state.offer.offerErrors[id] && !state.offer.offers[id]?.isSoldOut
    }).length
  })
  const intl = useIntl()
  const { landmarkSearchSortByTypes, searchSortByTypes, SORT_OPTION_CUSTOMER_RATING } = useOfferListSortOptions()
  const pathname = useAppSelector((state) => state.router.location.pathname)
  const dispatch = useAppDispatch()
  const { toggleSearchVertical } = useGlobalSearchURLHashVertical()
  const isMobile = useScreenSizeOnly('mobile')
  const isDesktop = useScreenSizeUp('largeDesktop')
  const searchType = getSearchTypeFromFilters(filters)
  const params = useMemo(() => parseSearchString(windowSearch), [windowSearch])
  const mapViewURL = useMemo(() => `/search/map?${deleteSearchParams(windowSearch, 'sortBy')}`, [windowSearch])
  const pageSize = SEARCH_STREAMING_PAGE_SIZE

  const formRef = useRef<SearchFormRef>(null)

  const onBedbankSelectDatesPrompt = useCallback((offerId: string, offerType: App.OfferType, windowSearch: string) => {
    const paramsToSet = [{ paramName: 'offerType', paramValue: offerType }, { paramName: 'offerId', paramValue: offerId }]
    if (isMobile) {
      toggleSearchVertical('hotels', { withDateSelect: true, search: setManySearchParamValues(windowSearch, paramsToSet) })
    } else {
      history.replace({ search: setManySearchParamValues(windowSearch, paramsToSet) })
      formRef.current?.form.current.scrollIntoView()
      formRef.current?.openDatePickerMenu()
    }
  }, [history, isMobile, toggleSearchVertical])
  const queryParams = useQueryParams()
  const { globalHotelsSearchDispatch, globalHotelsSearchState } = useGlobalHotelsSearchContext({
    ...GLOBAL_SEARCH_INITIAL_STATE,
    searchType,
    onBedbankSelectDatesPrompt,
  }, queryParams)

  const {
    searchTargetLabel,
  } = useMemo(() => ({
    searchTargetLabel: params.landmarkName || params.destinationName || params.propertyName,
    offerDistanceFromSearchTarget: searchResultMetadata.distanceFromSearchTarget[params.landmarkId ?? params.destinationId ?? ''],
  }), [
    params.landmarkId,
    params.landmarkName,
    params.destinationId,
    params.destinationName,
    params.propertyName,
    searchResultMetadata.distanceFromSearchTarget,
  ])

  const luxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const enabledFilters: EnabledFilters = useMemo(() => (
    {
      inclusions: true,
      amenities: true,
      customerRating: true,
      priceFilter: config.ENABLE_HOTEL_SEARCH_PAGE_PRICE_FILTER,
      holidayTypes: searchTargetLabel !== ANYWHERE_NAME,
      propertyTypes: searchTargetLabel !== ANYWHERE_NAME,
      locations: true,
      offerTypes: config.ENABLE_VILLA_RENTALS,
      popularFilters: true,
      luxPlusFeatures: luxPlusEnabled,
      agentHubFeatures: config.agentHub.isEnabled,
    }
  ), [searchTargetLabel, luxPlusEnabled])

  const defaultRooms = useMemo((): Array<App.Occupants> => [
    { adults: 2 },
  ], [])

  const listFilters = useMemo((): App.OfferListFilters => {
    const placeId = (placeIds && placeIds.length > 0) ? placeIds[0] : ANYWHERE_PLACE_ID
    const destinationId = filters.destinationId ? filters.destinationId : placeId
    return {
      ...filters,
      campaigns,
      offerTypes,
      rooms: filters.rooms?.length ? filters.rooms : defaultRooms,
      destinationId,
    }
  }, [placeIds, filters, campaigns, offerTypes, defaultRooms])

  const onPageChange = useCallback((page: number) => {
    dispatch(replace({ search: setSearchParamValue(windowSearch, 'pages', page) }))
    // eslint-disable-next-line
    }, [windowSearch])

  const hotelOfferList = useOfferList(listFilters, { disabled: hasOverrideOfferIds })

  const overrideOfferListAndAction = useMemo<OfferListAndAction | undefined>(() => {
    if (!hasOverrideOfferIds) return undefined
    return {
      action: OfferListAction.OVERRIDE,
      offerList: { offerIds: offerIds, fetching: false, error: null, key: 'contentful-override' },
    }
  }, [hasOverrideOfferIds, offerIds])

  const sortOptions = useMemo(() => {
    const options = searchType === 'landmark' ? landmarkSearchSortByTypes : searchSortByTypes

    return [...options, SORT_OPTION_CUSTOMER_RATING]
  }, [searchType, SORT_OPTION_CUSTOMER_RATING, landmarkSearchSortByTypes, searchSortByTypes])

  const handleFilterChange = useCallback<FilterControlChangeHandler>((params) => {
    const parsedSearch = parseSearchString(params.search)
    dispatch(setSearchEvent(
      getSearchKey(parsedSearch),
      { searchEvent: { type: 'filters' } },
    ))
    const additionalParams = [{ paramName: 'adults', paramValue: 2 }, { paramName: 'children', paramValue: 'none' }]
    if (pathname.slice(3) in REDIRECTION_PARAMS_MAP) {
      additionalParams.push(...REDIRECTION_PARAMS_MAP[pathname.slice(3)])
    } else if (!hasSearchTarget(parsedSearch)) {
      additionalParams.push(
        { paramName: 'destinationName', paramValue: ANYWHERE_NAME },
        { paramName: 'destinationId', paramValue: ANYWHERE_PLACE_ID },
      )
    }
    const search = setManySearchParamValues(params.search, additionalParams)
    dispatch(pushWithRegion('/search', search, undefined, params.hash))
  }, [dispatch, pathname])

  const handleSortChange = useCallback<SortControlChangeHandler>((params) => {
    const parsedSearch = parseSearchString(params.search)
    dispatch(setSearchEvent(
      getSearchKey(parsedSearch),
      { searchEvent: { type: 'sort' } },
    ))
    const additionalParams = [{ paramName: 'adults', paramValue: 2 }, { paramName: 'children', paramValue: 'none' }]
    if (pathname.slice(3) in REDIRECTION_PARAMS_MAP) {
      additionalParams.push(...REDIRECTION_PARAMS_MAP[pathname.slice(3)])
    } else if (!hasSearchTarget(parsedSearch)) {
      additionalParams.push(
        { paramName: 'destinationName', paramValue: ANYWHERE_NAME },
        { paramName: 'destinationId', paramValue: ANYWHERE_PLACE_ID },
      )
    }
    const search = setManySearchParamValues(params.search, additionalParams)
    dispatch(pushWithRegion('/search', search, undefined))
  }, [dispatch, pathname])

  const searchListTotal = searchType === 'property' || (!hotelOfferList.offerCount || filters.limit)
    ? hotelOfferList.offerIds.length
    : hotelOfferList.offerCount

  const mainListTotal = hasOverrideOfferIds ? liveOverrideOfferCount : searchListTotal

  const filterCount = useMemo(() => {
    return getAppliedFilterCount(listFilters, enabledFilters)
  }, [listFilters, enabledFilters])

  const hasFiltersApplied = filterCount > 0
  const mainListFetching = hotelOfferList.fetching
  const showFilterPane = showFilters && isDesktop && (mainListTotal > 0 || mainListFetching || hasFiltersApplied)

  const offerListFiltersProps = {
    sortOptions,
    searchType,
    switchView: {
      label: intl.formatMessage({
        id: 'tKP3X7BzeJthFuBwBgM9',
        defaultMessage: 'Map view',
        description: 'Label for the switch-to-map-view button on the contentful offer list page',
      }),
      view: 'map' as const,
      URL: mapViewURL,
    },
    listFilters,
  }

  const extras: Array<OfferListV2Item> = [{
    id: 'no-results',
    position: 'start',
    element: <HotelSearchNoSearchResultSection searchType={searchType} />,
    show: hasOverrideOfferIds ? mainListTotal === 0 : 'hasNoOffers',
  },
  {
    id: 'heading',
    position: 'start',
    element: <OfferCountAndSort
      destinationName={params.destinationName}
      specificPlaces={specificPlaces}
      fetching={mainListFetching}
      mainListTotal={mainListTotal}
      sortOptions={sortOptions}
      isFlexible={isFlexibleSearch}
      filters={listFilters}
      windowSearch={windowSearch}
      shouldShowFilters={!showFilterPane}
      showHolidayTypes={searchTargetLabel !== ANYWHERE_NAME}
      searchType={searchType}
      enabledFilters={enabledFilters}
      onFilterChange={handleFilterChange}
      onSortChange={handleSortChange}
    />,
  },
  {
    id: 'recommendations',
    position: 'end',
    element: <OfferRecommendations useList trackingDescription="Search Results" />,
    show: searchType === 'property',
  },
  ]

  return (
    <AnalyticsPageContext.Provider value={AnalyticsPage.search}>
      <GlobalSearchContextProvider
        state={globalHotelsSearchState}
        dispatch={globalHotelsSearchDispatch}
      >
        {showSearchHeader && <HotelSearchHeader
          filters={listFilters}
          formRef={formRef}
          offerListFiltersProps={offerListFiltersProps}
          searchVertical="hotels"
          baseSearchPath={pathname.slice(3)}
          baseSearchMapPath="/search/map"
          onFilterChange={handleFilterChange}
          onSortChange={handleSortChange}
        />}

        <StlyedPane className={clsx({ fullWidth: isMobile })}>
          <StyledLayoutContainer size={showFilterPane ? 'xl' : 'lg'} className={clsx({ filterPane: showFilterPane, fullWidth: isMobile })}>
            <StyledVerticalSpacer gap={16}>
              <OfferList
                filters={hasOverrideOfferIds ? undefined : listFilters}
                extraOfferListAndAction={overrideOfferListAndAction}
                interstitials={extras}
                onPageChange={onPageChange}
                tileStyle="search"
                eagerLoadFirstImage
                pageSize={hasOverrideOfferIds ? OVERRIDE_PAGE_SIZE : pageSize}
                showLoadMoreTiles={!hasOverrideOfferIds || (offerIds?.length ?? 0) > OVERRIDE_PAGE_SIZE}
                defaultPage={params.pages ? parseInt(params.pages) : 1}
                fullWidth={isMobile}
                isInfiniteScroll={isInfiniteScroll}
              />
            </StyledVerticalSpacer>
            {showFilterPane && <StyledHotelSearchPageFilterPane
              filters={listFilters}
              mapViewURL="" // keep this empty to prevent the map view button from showing
              search={windowSearch}
              enabledFilters={enabledFilters}
              hotelOfferList={hotelOfferList}
              onFilterChange={handleFilterChange}
            />}
          </StyledLayoutContainer>
        </StlyedPane>
      </GlobalSearchContextProvider>
    </AnalyticsPageContext.Provider>
  )
}

export default withRouter(connector(ContentfulVerticalOfferList))
